import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import RichTextInput from 'assets/components/inputs/richTextInput/RichTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { set } from 'lodash';
import StatementBalanceEmailTemplateTags from 'module/management/components/statementBalanceEmailTemplate/StatementBalanceEmailTemplateTags';
import { useEffect, useState } from 'react';
import { useStatementBalanceProvider } from '../StatementBalance.Hooks';

const StatementBalanceSendEmail = () => {
  const { lang } = getLocales();
  const { pageRouter, sendEmailApi, selected, getEmailTemplate, canModifyEmail } = useStatementBalanceProvider();

  const [defaultValues, setDefaultValues] = useState<Partial<Api.Core.StatementBalance.SendEmail.IRequest>>();

  useEffect(() => {
    (async () => {
      const template = await getEmailTemplate(selected?.dealer?.providerId, selected?.dealer?.documentLanguageIsoCode);

      setDefaultValues({
        emailTemplate: {
          ...template,
          additionalRecipientEmails: [...(template?.additionalRecipientEmails || [])],
        } as Model.IStatementBalanceEmailTemplate,
      });
    })();
  }, [selected?.dealer?.providerId, selected?.dealer?.documentLanguageIsoCode]);

  return (
    <ModalForm<Api.Core.StatementBalance.SendEmail.IRequest>
      subcomponents={ThemeModalFormDefault}
      title={lang.statementBalance}
      dependencyArray={[selected, defaultValues]}
      default={defaultValues}
      disabled={!defaultValues}
      onCancel={pageRouter.updateParams}
      onSubmit={(data) =>
        sendEmailApi.execute({ statementBalanceId: Number(pageRouter.params?.id), emailTemplate: data?.emailTemplate })
      }
      validation={(data, errors) => {
        if (
          data?.emailTemplate &&
          !data?.emailTemplate?.additionalRecipientEmails?.some((it) => it) &&
          !data?.emailTemplate?.recipientsWithRole?.some((it) => it.id) &&
          !data?.emailTemplate?.sendToRepresentative &&
          !data?.emailTemplate?.sendToDealersAccounting
        )
          set(errors, 'emailTemplate.additionalRecipientEmails', lang.mustNotBeEmpty);
        if (
          data?.emailTemplate?.additionalRecipientEmails?.length &&
          data?.emailTemplate?.additionalRecipientEmails?.some((it) => !isEmail(it))
        )
          set(errors, 'emailTemplate.additionalRecipientEmails', lang.invalidEmailFormatRequires);
        if (data?.emailTemplate && !data?.emailTemplate?.name)
          set(errors, 'emailTemplate.name', `${lang.mustNotBeEmpty}: ${lang.name}`);
        if (data?.emailTemplate && !data?.emailTemplate?.senderEmail)
          set(errors, 'emailTemplate.senderEmail', `${lang.mustNotBeEmpty}: ${lang.senderEmail}`);
        if (data?.emailTemplate?.senderEmail && !isEmailDomain(data?.emailTemplate?.senderEmail, 'goautoshield.com'))
          set(errors, 'emailTemplate.senderEmail', lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com'));
        if (data?.emailTemplate && !data?.emailTemplate?.subject)
          set(errors, 'emailTemplate.subject', `${lang.mustNotBeEmpty}: ${lang.senderName}`);
        if (data?.emailTemplate && !data?.emailTemplate?.senderName)
          set(errors, 'emailTemplate.senderName', `${lang.mustNotBeEmpty}: ${lang.senderName}`);
        if (data?.emailTemplate && !data?.emailTemplate?.htmlBody)
          set(errors, 'emailTemplate.htmlBody', `${lang.mustNotBeEmpty}: ${lang.htmlBody}`);
      }}
      render={({ form }) => {
        function updateEmailTemplate(updatedEmailTemplate: Partial<Model.IStatementBalanceEmailTemplate>) {
          form.merge({ emailTemplate: updatedEmailTemplate });
        }

        return (
          <FormWrapper isLoading={!defaultValues || !selected}>
            <TextInput
              name="name"
              label={lang.name}
              value={form.data?.emailTemplate?.name}
              error={form.errors.emailTemplate?.name}
              onChange={updateEmailTemplate}
              disabled={!canModifyEmail}
              icon={<Icon class="if-quill-pen" />}
            />
            <InputWrapper>
              <TextInput
                name="senderName"
                label={lang.senderName}
                value={form.data?.emailTemplate?.senderName}
                error={form.errors.emailTemplate?.senderName}
                onChange={updateEmailTemplate}
                disabled={!canModifyEmail}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="senderEmail"
                label={lang.senderEmail}
                value={form.data?.emailTemplate?.senderEmail}
                error={form.errors.emailTemplate?.senderEmail}
                onChange={updateEmailTemplate}
                disabled={!canModifyEmail}
                icon={<Icon class="if-quill-pen" />}
              />
            </InputWrapper>
            <h2>{lang.sendTo}</h2>
            <CheckWrapper>
              <CheckInput
                name="sendToRepresentative"
                label={lang.representative}
                class={inputStyles.check.switch}
                value={form.data?.emailTemplate?.sendToRepresentative}
                onChange={updateEmailTemplate}
                htmlElementProps={{
                  title: form.data?.emailTemplate?.sendToRepresentative ? lang.disable : lang.enable,
                }}
              />
              <CheckInput
                name="sendToDealersAccounting"
                label={lang.dealersAccounting}
                class={inputStyles.check.switch}
                value={form.data?.emailTemplate?.sendToDealersAccounting}
                onChange={updateEmailTemplate}
                htmlElementProps={{
                  title: form.data?.emailTemplate?.sendToDealersAccounting ? lang.disable : lang.enable,
                }}
              />
            </CheckWrapper>
            <h2>{lang.additionalRecipientEmails}</h2>
            <DataMap
              data={[...(form.data?.emailTemplate?.additionalRecipientEmails || []), '']}
              render={({ data: email, index }) => (
                <TextInput
                  key={index}
                  name="email"
                  label={lang.email}
                  value={email}
                  error={form.errors.emailTemplate?.additionalRecipientEmails}
                  onChange={({ email }) => {
                    let additionalRecipientEmails = [...(form.data.emailTemplate?.additionalRecipientEmails || [])];
                    additionalRecipientEmails?.splice(index, 1, email);
                    additionalRecipientEmails = additionalRecipientEmails?.filter((it) => it);
                    form.update({ emailTemplate: { ...form.data.emailTemplate, additionalRecipientEmails } });
                  }}
                  icon={<Icon class="if-quill-pen" />}
                />
              )}
            />
            <h2>{lang.email}</h2>
            <TextInput
              name="subject"
              label={lang.subject}
              value={form.data?.emailTemplate?.subject}
              error={form.errors.emailTemplate?.subject}
              onChange={updateEmailTemplate}
              disabled={!canModifyEmail}
              icon={<Icon class="if-quill-pen" />}
            />
            <RichTextInput
              name="htmlBody"
              label={lang.htmlBody}
              value={form.data?.emailTemplate?.htmlBody}
              error={form.errors.emailTemplate?.htmlBody}
              onChange={updateEmailTemplate}
              icon={<Icon class="if-quill-pen" />}
              disabled={!canModifyEmail}
              customActions={<StatementBalanceEmailTemplateTags />}
            />
          </FormWrapper>
        );
      }}
    />
  );
};

export default StatementBalanceSendEmail;
